import { Link } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';
import { Drawer } from './Drawer';
import { QuantitySelector } from './QuantitySelector';
import { useCartStore, useCartTotals } from '../store/cartStore';
import { useCart } from '../hooks/useCart';
import { formatCurrency } from '../utils/formatCurrency';

export function CartDrawer() {
  const isOpen = useCartStore((s) => s.isOpen);
  const closeCart = useCartStore((s) => s.closeCart);
  const lines = useCartStore((s) => s.lines);
  const { subtotal } = useCartTotals();
  const { changeQuantity, removeFromCart } = useCart();

  return (
    <Drawer isOpen={isOpen} onClose={closeCart} title={`Your Bag${lines.length ? ` (${lines.length})` : ''}`}>
      {lines.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
          <ShoppingBag className="h-10 w-10 text-charcoal/30" aria-hidden="true" />
          <p className="text-sm text-charcoal/70">Your bag is empty.</p>
          <Link to="/shop" onClick={closeCart} className="btn-primary">
            Shop Toothbrushes
          </Link>
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <ul className="flex-1 divide-y divide-beige overflow-y-auto px-5">
            {lines.map((line, index) => (
              <li key={line.id || line.productId || `cart-item-${index}`} className="flex gap-4 py-5">
                <Link to={`/product/${line.slug}`} onClick={closeCart} className="shrink-0">
                  <img
                    src={line.image}
                    alt={line.name}
                    className="h-24 w-20 rounded bg-cream object-cover"
                  />
                </Link>
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/product/${line.slug}`}
                      onClick={closeCart}
                      className="text-sm font-medium text-charcoal hover:text-forest"
                    >
                      {line.name}
                    </Link>
                    <button
                      type="button"
                      aria-label={`Remove ${line.name} from bag`}
                      onClick={() => removeFromCart(line.id || line.productId)}
                      className="text-charcoal/40 transition-colors hover:text-error"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <QuantitySelector
                      quantity={line.quantity}
                      onChange={(q) => changeQuantity(line.id || line.productId, q)}
                    />
                    <span className="text-sm font-semibold text-charcoal">
                      {formatCurrency(line.price * line.quantity)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="border-t border-beige px-5 py-5">
            <div className="mb-4 flex items-center justify-between text-sm">
              <span className="text-charcoal/70">Subtotal</span>
              <span className="font-heading text-lg text-forest-dark">{formatCurrency(subtotal)}</span>
            </div>
            <p className="mb-4 text-xs text-charcoal/60">Shipping and taxes calculated at checkout.</p>
            <div className="flex flex-col gap-3">
              <Link to="/checkout" onClick={closeCart} className="btn-primary w-full">
                Checkout
              </Link>
              <Link to="/cart" onClick={closeCart} className="btn-secondary w-full">
                View Bag
              </Link>
              <button type="button" onClick={closeCart} className="text-center text-xs font-medium uppercase tracking-wide text-charcoal/60 hover:text-forest">
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
